import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  useTheme,
} from '@mui/material';
import PublicPageShell from '../components/PublicPageShell';
import LegalBusinessDetails from '../components/LegalBusinessDetails';

const PRIMARY = '#0087c1';

const SUB_PROCESSORS = [
  { provider: 'Microsoft Azure', purpose: 'Hosting, database, and file storage', data: 'All service data (encrypted at rest and in transit)' },
  { provider: 'OpenAI', purpose: 'AI assistant functionality', data: 'Prompts and attached files sent during assistant conversations' },
  { provider: 'Google Maps Platform', purpose: 'Reverse geocoding (converting coordinates to a text address)', data: 'Device coordinates (transiently, not stored by us)' },
  { provider: 'What3Words', purpose: 'Address lookup from coordinates', data: 'Device coordinates (transiently, not stored by us)' },
  { provider: 'Expo', purpose: 'Push notification delivery (mobile apps)', data: 'Device push notification identifier' },
];

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" component="h2" fontWeight={700} sx={{ mb: 2, color: 'text.primary' }}>
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const Paragraph = ({ children, sx }) => (
  <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2, ...sx }}>
    {children}
  </Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mb: 2 }}>
    {items.map((item, i) => (
      <Box component="li" key={i} sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

const SubHeading = ({ children }) => (
  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1, color: 'text.primary' }}>
    {children}
  </Typography>
);

export default function PrivacyPolicy() {
  const theme = useTheme();

  return (
    <PublicPageShell>
      <Helmet>
        <title>Privacy Policy — Struxor</title>
        <meta name="description" content="Read Struxor's Privacy Policy to understand how we collect, use, and protect your data on our construction software platform." />
        <link rel="canonical" href="https://www.struxorapp.com/privacy" />
        <meta property="og:title" content="Privacy Policy — Struxor" />
        <meta property="og:description" content="Read Struxor's Privacy Policy to understand how we collect, use, and protect your data on our construction software platform." />
        <meta property="og:url" content="https://www.struxorapp.com/privacy" />
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Privacy Policy
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 550, mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}>
            How we collect, use, and protect your data.
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        <Paragraph sx={{ mb: 4, fontStyle: 'italic' }}>Last updated: 6 April 2026</Paragraph>

        <Paragraph>
          This Privacy Policy explains how <strong>Luke Rutley Ltd</strong>, trading as Struxor ("we", "us"),
          collects and uses personal data across our web application and mobile apps (iOS and Android) in
          line with UK GDPR.
        </Paragraph>

        <Box sx={{ mb: 5, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Controller Information
          </Typography>
          <LegalBusinessDetails />
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* 1 */}
        <Section number={1} title="Information We Collect">
          <SubHeading>All Platforms (Web, iOS, and Android)</SubHeading>
          <BulletList items={[
            <><strong>Account information</strong> — name, email address, organisation name, and login credentials.</>,
            <><strong>Project and work content</strong> — project data, activity notes, timesheet entries, digital forms, purchase orders, risk assessments and method statements (RAMS), signatures, and other content you create within the Service. This data is linked to your account and used solely to provide app functionality.</>,
            <><strong>Photos and videos</strong> — photos you attach to project activity notes, forms, or plan markups are uploaded and stored within your organisation's project data. Photos are linked to your account and used solely for app functionality.</>,
            <><strong>Precise location</strong> — when you clock in or clock out, the app requests your device location to verify proximity to the work site. Location coordinates are used only for this real-time check and are <strong>not stored</strong>. You may also optionally use your current location to auto-fill a project address; in this case the coordinates are converted to a text address server-side and then discarded. On the web app, location is accessed via your browser; on mobile, it is accessed via device location services with your permission.</>,
            <><strong>AI assistant interactions</strong> — prompts, attached files, and outputs processed to provide AI assistant functionality. Conversations are stored against your account so you can resume them later.</>,
            <><strong>Customer support</strong> — support requests, emails, and feedback you send to us. This data is linked to your account and used for app functionality and to improve the Service.</>,
          ]} />

          <SubHeading>Web App Only</SubHeading>
          <BulletList items={[
            <><strong>Billing information</strong> — payment details collected and processed securely by our third-party payment provider. We do not store your full payment card details.</>,
          ]} />

          <SubHeading>Mobile Apps Only (iOS and Android)</SubHeading>
          <BulletList items={[
            <><strong>Push notifications</strong> — if you allow notifications, we send push notifications to your device for project updates. You can disable notifications at any time in your device settings.</>,
          ]} />

          <SubHeading>What We Do Not Collect</SubHeading>
          <BulletList items={[
            <>We do <strong>not</strong> collect location data in the background. Location is only accessed while the app is in use and only when you perform a clock in, clock out, or address lookup action.</>,
            <>We do <strong>not</strong> collect device identifiers, advertising IDs, or usage analytics beyond what is described above.</>,
            <>We do <strong>not</strong> use tracking technologies to follow you across other apps or websites.</>,
          ]} />
        </Section>

        {/* 2 */}
        <Section number={2} title="How We Use Information">
          <BulletList items={[
            'To provide and improve the Service.',
            'To process payments and manage subscriptions (web app).',
            'To provide support and communicate with you.',
            'To verify user location at clock in/out (all platforms).',
            'To deliver push notifications for project updates (mobile apps).',
            'To provide AI assistant functionality.',
            'To ensure security, prevent misuse, and comply with legal obligations.',
          ]} />
          <Paragraph>
            We do <strong>not</strong> sell personal data. We do <strong>not</strong> use your data for tracking or advertising.
          </Paragraph>
        </Section>

        {/* 3 */}
        <Section number={3} title="Data Processing & Storage">
          <BulletList items={[
            <>Luke Rutley Ltd is the <strong>data controller</strong> for personal data collected when you browse our website, request a demo, create an account, purchase a subscription, or contact us.</>,
            <>Customer organisations are normally the <strong>data controllers</strong> for workspace content they upload to the Service, and Luke Rutley Ltd acts as a <strong>data processor</strong> for that content when hosting and processing it on their behalf.</>,
            'Data is stored securely on Microsoft Azure infrastructure, including Azure SQL Server and Azure Blob Storage.',
          ]} />
        </Section>

        {/* 4 */}
        <Section number={4} title="Legal Basis for Processing">
          <Paragraph>
            We process personal data under the following lawful bases:
          </Paragraph>
          <BulletList items={[
            <><strong>Contract</strong> - to provide and operate the Struxor service, including account creation, subscription management, and core platform functionality.</>,
            <><strong>Legitimate interests</strong> - to improve and secure the platform, including monitoring performance, investigating issues, and preventing abuse or fraud.</>,
            <><strong>Legal obligation</strong> - to comply with applicable laws, including financial record keeping and responding to lawful requests.</>,
            <><strong>Consent</strong> - where applicable, including optional communications and device-level permissions such as location access or push notifications.</>,
          ]} />
        </Section>

        {/* 5 */}
        <Section number={5} title="Cookies & Similar Technologies">
          <Paragraph>
            We do not use cookies for analytics, advertising, or tracking purposes.
          </Paragraph>
          <Paragraph>
            We only use strictly necessary cookies required for the operation of the service, such as:
          </Paragraph>
          <BulletList items={[
            'Authentication and session management.',
            'Security and fraud prevention.',
          ]} />
          <Paragraph>
            These cookies are essential for the website to function and cannot be disabled.
          </Paragraph>
          <Paragraph>
            No third-party tracking cookies are used.
          </Paragraph>
        </Section>

        {/* 6 */}
        <Section number={6} title="Third-Party Services & Sub-Processors">
          <Paragraph>
            We use the following third-party services to operate the Service:
          </Paragraph>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                  <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Data Shared</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {SUB_PROCESSORS.map((sp) => (
                  <TableRow key={sp.provider}>
                    <TableCell sx={{ fontWeight: 600 }}>{sp.provider}</TableCell>
                    <TableCell>{sp.purpose}</TableCell>
                    <TableCell>{sp.data}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paragraph>
            We do <strong>not</strong> share photos, location data, or user content with any third parties for their
            own purposes. Third-party services receive only the minimum data required to perform their function.
          </Paragraph>
        </Section>

        {/* 7 */}
        <Section number={7} title="Data Retention">
          <BulletList items={[
            'Account data is retained while your subscription is active.',
            "Photos and project data are retained as part of your organisation's project records until deleted by an authorised user or upon account closure.",
            'Location coordinates are never stored and exist only for the duration of the clock in/out verification or address lookup request.',
            'AI assistant conversation history is retained while your account is active.',
            'You may request deletion of your data at any time.',
            'We may retain limited records where legally required (e.g., invoices).',
          ]} />
        </Section>

        {/* 8 */}
        <Section number={8} title="Security">
          <Paragraph>
            We apply measures including encryption in transit (TLS) and at rest, access controls, tenant data isolation,
            and regular backups. All inter-service communication uses encrypted connections.
          </Paragraph>
        </Section>

        {/* 9 */}
        <Section number={9} title="Your Rights">
          <Paragraph>Under UK GDPR you may:</Paragraph>
          <BulletList items={[
            'Access, correct, or delete your data.',
            'Restrict or object to processing.',
            'Port your data to another provider.',
            'Withdraw consent at any time (e.g., for location or push notifications via your device settings).',
          ]} />
          <Paragraph>
            You have the right to lodge a complaint with the UK's supervisory authority, the Information Commissioner's Office (ICO), if you believe we have not handled your data appropriately.
          </Paragraph>
          <Paragraph>
            Website: <a href="https://ico.org.uk/" target="_blank" rel="noreferrer">https://ico.org.uk/</a>
          </Paragraph>
          <Paragraph>
            To exercise your rights, contact us at the address below.
          </Paragraph>
        </Section>

        {/* 10 */}
        <Section number={10} title="International Transfers">
          <Paragraph>
            We use the OpenAI service for AI assistant functionality, which may process data outside the UK.
          </Paragraph>
        </Section>

        {/* 11 */}
        <Section number={11} title="Children's Privacy">
          <Paragraph>
            The Service is not directed at children under 16. We do not knowingly collect personal data from children.
          </Paragraph>
        </Section>

        {/* 12 */}
        <Section number={12} title="Changes to This Policy">
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify you of material changes via the
            Service or by email. Continued use after changes constitutes acceptance.
          </Paragraph>
        </Section>

        {/* 13 */}
        <Section number={13} title="Contact">
          <Paragraph>
            To exercise your rights, make a privacy enquiry, or send a formal notice, contact Luke Rutley Ltd using the details below.
          </Paragraph>
          <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
            <LegalBusinessDetails />
          </Box>
        </Section>
      </Container>
    </PublicPageShell>
  );
}
